import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Box,
  Anchor,
} from '@mantine/core';
import { useAuth } from '../../contexts/AuthContext';
import { ErrorDisplay } from '../common/ErrorDisplay';
import {
  createClientValidationError,
  DisplayableError,
  errorHandler,
} from '../../services/utils/errorHandler';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<DisplayableError | null>(null);
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const clearError = () => {
    setError(null);
  };

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    clearError();
    setLoading(true);

    // Validation checks
    if (!username || !password) {
      setError(
        createClientValidationError('Please fill in all fields', {
          username: username ? '' : 'Username is required',
          password: password ? '' : 'Password is required',
        })
      );
      setLoading(false);
      return;
    }

    // Attempt login
    const result = await login(username, password);

    if (result.success) {
      navigate('/trips');
    } else {
      setError(
        result.processedError ??
          (result.error
            ? errorHandler.processError(new Error(result.error))
            : null)
      );
    }
    setLoading(false);
  };

  return (
    <Container size="xs" mt="xl">
      <Box ta="center">
        <Paper shadow="md" p="xl" radius="md" w="100%">
          <Title order={1} ta="center" mb="lg">
            Sign In
          </Title>

          {error && <ErrorDisplay error={error} className="mb-4" />}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextInput
              label="Username or Email"
              placeholder="Enter your username or email"
              required
              id="username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={e => setUsername(e.target.value)}
              mb="md"
            />
            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              required
              name="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              mb="xl"
            />
            <Button
              type="submit"
              data-cy="login-submit"
              fullWidth
              variant="filled"
              size="md"
              loading={loading}
              disabled={loading}
              mb="md"
            >
              Sign In
            </Button>
            <Box ta="center">
              <Anchor
                component={RouterLink}
                to="/signup"
                size="sm"
                data-cy="login-to-signup"
              >
                {"Don't have an account? Sign Up"}
              </Anchor>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
